import anime from 'animejs';

export class Slider {
    private static readonly interval: number = 8000;
    private static readonly fade: number = 500;
    private static readonly maxAnimation: number = 20;
    private static readonly zoom: number = 1.2;

    private readonly slider?: HTMLElement;
    private readonly sliderItems?: HTMLElement[];
    private readonly images?: string[];

    private previousImage?: HTMLImageElement;
    private currentImage?: HTMLImageElement;
    private reverse = false;

    private i = 0;

    public constructor() {
        this.slider = document.querySelector<HTMLElement>('#hero') ?? undefined;

        if (!this.slider) {
            return;
        }

        this.sliderItems = [ ...this.slider.children ] as HTMLElement[];

        this.images = this.sliderItems.
            map((sliderElement): string => sliderElement.dataset.url ?? '').
            filter((image): boolean => Boolean(image)).
            map((image): string => image);

        this.images = this.shuffle(this.images);

        this.sliderItems.forEach((sliderElement): void => {
            if (!this.slider) {
                return;
            }

            this.slider.removeChild(sliderElement);
        });
    }

    public startRotation(): void {
        this.tick();
    }

    private tick(): void {
        if (!this.slider || !this.images) {
            return;
        }

        if (this.images.length <= 0) {
            return;
        }

        this.currentImage = this.createImageElement();

        this.currentImage.onload = (): void => {
            if (this.currentImage) {
                this.animate(this.currentImage);
            }

            setTimeout((): void => {
                this.tick();
            }, Slider.interval);
        };

        this.currentImage.src = this.images[this.i];

        this.removePreviousImage();
        this.addCurrentImage();
        this.preloadNextImage();

        this.i = this.getSafeIndex(this.i + 1);
        this.reverse = !this.reverse;
    }

    private animate(image: HTMLImageElement): void {
        /* eslint-disable no-param-reassign */
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        const ratioScreen = windowWidth / windowHeight;
        const ratioImage = image.naturalWidth / image.naturalHeight;

        let animation = 0;
        let maxAnimation = 0;
        let offsetDivisor = 0;

        if (ratioImage > ratioScreen) {
            image.style.height = `${windowHeight}px`;
            image.style.width = 'auto';

            animation = Math.round((windowHeight * ratioImage) - windowWidth);
            maxAnimation = (windowHeight / 100) * Slider.maxAnimation;
            offsetDivisor = 2;
        } else {
            image.style.height = 'auto';
            image.style.width = `${windowWidth}px`;

            animation = Math.round((windowWidth / ratioImage) - windowHeight);
            maxAnimation = (windowWidth / 100) * Slider.maxAnimation;
            offsetDivisor = 3;
        }

        let offset = 0;

        if (animation > maxAnimation) {
            offset = (animation - maxAnimation) / offsetDivisor;
            animation = maxAnimation;
        }
        animation = -animation;
        if (this.reverse) {
            animation = -animation;
            offset += animation;
        }

        if (ratioImage > ratioScreen) {
            image.style.left = `${-offset}px`;

            anime({
                targets: image,
                translateX: [{ value: animation, duration: Slider.interval, delay: 0 }],
                opacity: [{ value: 1, duration: Slider.fade, delay: 0 }],
                scale: [{ value: 1, duration: Slider.interval, delay: 0 }],
                easing: 'easeInOutQuad',
                autoplay: true
            });
        } else {
            image.style.top = `${-offset}px`;

            anime({
                targets: image,
                translateY: [{ value: animation, duration: Slider.interval, delay: 0 }],
                opacity: [{ value: 1, duration: Slider.fade, delay: 0 }],
                scale: [{ value: 1, duration: Slider.interval, delay: 0 }],
                easing: 'easeInOutQuad',
                autoplay: true
            });
        }
        /* eslint-enable no-param-reassign */
    }

    private removePreviousImage(): void {
        setTimeout((): void => {
            if (this.slider && this.previousImage) {
                this.slider.removeChild(this.previousImage);
            }

            this.previousImage = this.currentImage;
        }, Slider.fade);
    }

    private addCurrentImage(): void {
        if (!this.slider || !this.currentImage) {
            return;
        }

        this.slider.appendChild(this.currentImage);
    }

    private preloadNextImage(): void {
        if (!this.images) {
            return;
        }

        const nextImage = document.createElement('img');
        const i = this.getSafeIndex(this.i + 1);

        nextImage.src = this.images[i];
    }

    private createImageElement(): HTMLImageElement {
        const image = document.createElement('img');

        image.style.position = 'absolute';
        image.style.top = '0';
        image.style.left = '0';
        image.style.maxWidth = 'none';
        image.style.maxHeight = 'none';
        image.style.opacity = '0';
        image.style.transform = `scale(${Slider.zoom.toString(10)})`;

        return image;
    }

    private getSafeIndex(i: number): number {
        if (!this.images) {
            return 0;
        }

        return (i + this.images.length) % this.images.length;
    }

    private shuffle(images: string[]): string[] {
        for (let i = images.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * i);
            const temp = images[i];

            /* eslint-disable no-param-reassign */
            images[i] = images[j];
            images[j] = temp;
            /* eslint-enable no-param-reassign */
        }

        return images;
    }
}
